import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Query, Mutation } from 'react-apollo';
import { compose } from 'redux';
import cn from 'classnames';
import { ActivityList, CropList, CropForm, FieldDetails } from 'containers';
import {
  Button,
  MapComponent,
  Modal,
  Tab,
  ErrorBoundary,
  Online,
} from 'common/components';
import { isDesktop } from 'common/utils';
import { MUTATION_DELETE_CROP } from 'api/crop';
import { store } from 'store';
import {
  MUTATION_DELETE_FIELD,
  FETCH_FIELD_DETAILS_WITH_NAME,
} from 'api/field';
import {
  FETCH_ALL_EXCEPT_ACTIVITIES,
  MUTATION_DELETE_FIELD_ACTIVITY,
} from 'api/activity';
import { toAcres } from 'util/conversions';
import { generateFieldId } from 'util/ids';
import * as constants from 'common/constants';
import { getHarvestedFlag } from 'common/utils';
import { base64_decode } from 'util/base64';
import { Redirect } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import './index.css';

const desktop = isDesktop();
export class FieldDetail extends Component {
  static displayName = 'FieldDetail';

  static propTypes = {
    match: PropTypes.object,
    onDelete: PropTypes.func,
    goToCropList: PropTypes.func.isRequired,
    activityList: PropTypes.arrayOf(PropTypes.object),
    season: PropTypes.number.isRequired,
    mapFields: PropTypes.arrayOf(PropTypes.object),
    updateMapFields: PropTypes.func.isRequired,
    goToFieldList: PropTypes.func.isRequired,
  };

  static defaultProps = {
    mapFields: [],
  };

  state = {
    confirmActive: false,
    selectedCrop: {},
    deleteFieldResponse: {},
  };

  render() {
    const {
      match: {
        params: { encodedFieldName, id },
      },
      season,
    } = this.props;
    const { selectedCrop } = this.state;
    // TODO: get the panel height to set field__crops height (DetailsPanel needs)
    // const panelSize = `100% - ${this.panel ? this.panel.offsetHeight : 0}px`;
    const fieldname = base64_decode(encodedFieldName);

    return (
      <ErrorBoundary>
        <Mutation
          mutation={MUTATION_DELETE_CROP}
          onCompleted={({ deleteCropForField }) => {
            if (deleteCropForField) {
              mixpanel.track(constants.ANALYTICS.EVENTS.CROP.DELETED);
            }
            this._toggleCropModal();
          }}
          update={(cache, { data: { deleteCropForField } }) => {
            const variables = {
              season,
              active: true,
              name: fieldname,
              id: generateFieldId(fieldname),
            };
            const { getAllCropsForField, ...rest } = cache.readQuery({
              query: FETCH_FIELD_DETAILS_WITH_NAME,
              variables,
            });
            cache.writeQuery({
              query: FETCH_FIELD_DETAILS_WITH_NAME,
              variables,
              data: {
                getAllCropsForField: getAllCropsForField.filter(
                  crop => crop.id !== deleteCropForField.id
                ),
                ...rest,
              },
            });
          }}
        >
          {deleteCrop => (
            <Mutation
              mutation={MUTATION_DELETE_FIELD_ACTIVITY}
              update={(cache, { data: { deleteActivityForField } }) => {
                const variables = {
                  season,
                  active: true,
                  name: fieldname,
                  id: generateFieldId(fieldname),
                };
                const { getAllActivitiesForField, ...rest } = cache.readQuery({
                  query: FETCH_FIELD_DETAILS_WITH_NAME,
                  variables,
                });
                cache.writeQuery({
                  query: FETCH_FIELD_DETAILS_WITH_NAME,
                  variables,
                  data: {
                    getAllActivitiesForField: getAllActivitiesForField.filter(
                      crop => crop.id !== deleteActivityForField.id
                    ),
                    ...rest,
                  },
                });
              }}
            >
              {deleteActivityForField => (
                <Query
                  query={FETCH_FIELD_DETAILS_WITH_NAME}
                  fetchPolicy="cache-and-network"
                  variables={{
                    season,
                    active: true,
                    name: fieldname,
                    id: generateFieldId(fieldname),
                  }}
                >
                  {({ loading, error, data }) => {
                    if (loading || error) return null;
                    if (data.getField === null) return <Redirect to="/error" />;

                    const {
                      getField: field,
                      getAllCropsForField: frzCrops,
                      getAllActivitiesForField,
                    } = data;
                    const crops =
                      frzCrops &&
                      JSON.parse(JSON.stringify(frzCrops)).sort(
                        ({ plantedArea: c1 }, { plantedArea: c2 }) =>
                          toAcres(c1.size, c1.type) < toAcres(c2.size, c2.type)
                      );

                    return (
                      <Fragment>
                        <div
                          className={cn(
                            'field-wrapper__wrapper field-wrapper--mapstatic columns is-gapless',
                            id && 'columns--white'
                          )}
                        >
                          {this._getFieldMapFragment(field, crops)}
                          <aside className="field-wrapper__aside column is-one-third is-gapless">
                            <div className="field-wrapper__aside__header">
                              <FieldDetails
                                field={field}
                                season={season}
                                deleteAction={this._toggleFieldModal}
                                id={generateFieldId(fieldname)}
                              />
                            </div>
                            <div className="field-wrapper__aside__content column__inner">
                              {this._getTabPanel(
                                crops,
                                field,
                                getAllActivitiesForField
                              )}
                            </div>
                          </aside>
                        </div>

                        {this._getFieldModal(season, fieldname)}
                        {selectedCrop &&
                          selectedCrop.id &&
                          this._getCropModal(
                            deleteCrop,
                            deleteActivityForField,
                            getAllActivitiesForField
                          )}
                      </Fragment>
                    );
                  }}
                </Query>
              )}
            </Mutation>
          )}
        </Mutation>
      </ErrorBoundary>
    );
  }

  _toggleFieldModal = () => {
    this.setState({ confirmActive: !this.state.confirmActive });
  };

  _toggleCropModal = selectedCrop => {
    selectedCrop
      ? this.setState({ selectedCrop })
      : this.setState({ selectedCrop: null });
  };

  _getFieldModal(season, fieldname) {
    const { goToFieldList } = this.props;
    const { deleteFieldResponse } = this.state;

    let modalText = (
      <small>
        This field will not be available in the future seasons.
        <br />
        <strong>Are you sure you want to delete this field?</strong>
      </small>
    );

    if (deleteFieldResponse.message) {
      const deleteFieldErrorMessage = deleteFieldResponse.message.replace(
        'GraphQL error: ',
        ''
      );
      modalText = (
        <small>
          <strong className="fieldModal__error">
            {deleteFieldErrorMessage}
          </strong>
        </small>
      );
    }

    return (
      this.state.confirmActive && (
        <Modal
          onToggle={this._toggleFieldModal}
          theme={['narrow']}
          header={'Delete Field'}
        >
          {modalText}
          <section className={'field__group'}>
            <fieldset className={'field text--center'}>
              <Mutation
                refetchQueries={[
                  {
                    query: FETCH_ALL_EXCEPT_ACTIVITIES,
                    variables: {
                      active: true,
                      season: store.getState().settings.season,
                    },
                  },
                ]}
                mutation={MUTATION_DELETE_FIELD}
                onError={error => {
                  // is this the right place to start passing errors to other components?
                  this.setState({
                    deleteFieldResponse: error,
                  });
                }}
                onCompleted={({ deleteField }) => {
                  if (deleteField && deleteField.size) {
                    mixpanel.track(constants.ANALYTICS.EVENTS.FIELD.DELETED);
                    const newSize = -deleteField.size.size;
                    mixpanel.people.increment({
                      [constants.ANALYTICS.PROPERTIES.FIELD
                        .AREA_MAPPED]: newSize,
                    });
                  }

                  goToFieldList();
                  this._toggleFieldModal();
                }}
              >
                {deleteField =>
                  this._getDeleteFieldButtons(deleteField, fieldname)
                }
              </Mutation>
            </fieldset>
            <fieldset className={'field text--center'}>
              <Button
                className={
                  deleteFieldResponse.message ? 'btn btn--primary' : 'btn'
                }
                onClick={this._toggleFieldModal}
              >
                {deleteFieldResponse.message ? 'Ok' : 'No'}
              </Button>
            </fieldset>
          </section>
        </Modal>
      )
    );
  }

  _getDeleteFieldButtons = (deleteField, fieldname) => {
    const { deleteFieldResponse } = this.state;

    let buttons = null;

    if (!deleteFieldResponse.message) {
      buttons = (
        <Button
          className="btn btn--primary"
          onClick={() =>
            deleteField({ variables: { id: generateFieldId(fieldname) } })
          }
        >
          Yes
        </Button>
      );
    }

    return buttons;
  };

  _getCropModal(deleteCrop, deleteActivityForField, activities) {
    const { id, crop } = this.state.selectedCrop;
    const {
      match: {
        params: { encodedFieldName },
      },
      mapFields,
      updateMapFields,
    } = this.props;
    const fieldName = base64_decode(encodedFieldName);

    // find the corresponding seeding activity
    const seedingActivity = activities.find(
      a =>
        a.fieldActivityType === constants.ACTIVITY_TYPES.SEEDING &&
        a.crop.name === crop.name &&
        a.crop.variety === crop.variety
    );

    return (
      <Modal
        onToggle={this._toggleCropModal}
        theme={['narrow']}
        header={'Delete Crop'}
      >
        <small>Are you sure you want to delete this crop?</small>
        <section className={'field__group'}>
          <fieldset className={'field text--center'}>
            <Button
              className="btn btn--primary"
              onClick={async () => {
                // update map fields
                const fields = mapFields.map(field => {
                  if (field && field.name === fieldName) {
                    return {
                      ...field,
                      crops: field.crops.filter(crop => crop.id !== id),
                    };
                  }
                  return field;
                });

                await deleteCrop({ variables: { id } });
                if (seedingActivity) {
                  await deleteActivityForField({
                    variables: { id: seedingActivity.id },
                  });
                }
                updateMapFields(fields);
              }}
            >
              Yes
            </Button>
          </fieldset>
          <fieldset className={'field text--center'}>
            <Button className="btn" onClick={this._toggleCropModal}>
              No
            </Button>
          </fieldset>
        </section>
      </Modal>
    );
  }

  _getFieldMapFragment(field, crops = []) {
    return (
      field &&
      (desktop ? (
        this._getMapComponent(field, crops)
      ) : (
        <Online>{this._getMapComponent(field, crops)}</Online>
      ))
    );
  }

  _getMapComponent = (field, crops) => (
    <MapComponent
      polygons={[
        {
          geojson: field.geojson ? JSON.parse(field.geojson) : null,
          paths: field.shape,
          title: field.name,
          size: field.size,
          harvested: getHarvestedFlag(crops),
          crops,
        },
      ]}
      showFields
      ignoreMapUpdate
    />
  );

  _getFieldCropsTab(crops, field, activities) {
    const {
      match: {
        params: { encodedFieldName, id },
      },
      goToCropList,
      season,
      mapFields,
    } = this.props;
    const fieldname = encodedFieldName && base64_decode(encodedFieldName);
    const ID = id && id !== 'add' ? base64_decode(id) : 'add';
    return (
      <Fragment>
        {!id && (
          <CropList
            setDeleteCrop={this._toggleCropModal}
            field={{ name: fieldname, size: field.size.size }}
            activities={activities}
            id={ID}
            crops={crops.length > 0 ? crops : []}
          />
        )}
        {id && (
          <CropForm
            id={ID}
            crops={
              crops && crops.length > 0
                ? crops.filter(crop => crop.id !== ID)
                : []
            }
            season={season}
            fieldName={fieldname}
            // plantedArea={availablePlantingArea}
            closeForm={() => goToCropList(encodedFieldName)}
            mapFields={mapFields}
          />
        )}
      </Fragment>
    );
  }
  _getFieldActivitiesTab(fieldActivities) {
    return (
      <ActivityList type="field" activities={fieldActivities} field inDetails />
    );
  }

  _getTabPanel(crops, field, activities) {
    return (
      <Tab
        tabs={[
          {
            label: 'Crops',
            content: this._getFieldCropsTab(crops, field, activities),
          },
          {
            label: 'Activities',
            content: this._getFieldActivitiesTab(activities || []),
          },
        ]}
      />
    );
  }
}

const mapState = ({ settings: { season }, map: { fields } }) => ({
  season,
  mapFields: fields,
});

const mapDispatch = {
  goToCropList: fieldname =>
    push(`${constants.ROUTE_FIELDS_DETAIL}/${fieldname}`),
  updateMapFields: fields => ({
    type: constants.UPDATE_MAP_FIELDS,
    payload: fields,
  }),
  goToFieldList: () => push(constants.ROUTE_FIELDS),
};

const enhance = compose(
  connect(
    mapState,
    mapDispatch
  )
);

export default enhance(FieldDetail);



// WEBPACK FOOTER //
// ./src/pages/Field/Detail/index.js